import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Box,
  Card,
  Flex,
  Text,
  Button,
  Spinner,
  Badge,
  Heading,
} from "@radix-ui/themes";
import {
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
  Line,
  LineChart,
} from "recharts";
import { format } from "date-fns";
import { apiClient } from "~/services/api-client";
import { useSharedDailyTimeseries } from "~/hooks/use-shared-daily-timeseries";
import CardHeader from "~/components/demo/card-header";

interface TimeseriesDataPoint {
  datetime: string;
  price: number;
  percent_move: number;
}

interface TimeseriesData {
  figi: string;
  price: number;
  previous_close: number;
  percent_move: number;
  timeseries: TimeseriesDataPoint[];
  open: number;
  close: number;
  volume: number;
}

interface SecurityInfo {
  symbol: string;
  name: string;
  sector: string;
  exchange: string;
  figi: string;
}

export interface PricePerformanceChartRef {
  refetchData: () => Promise<void>;
}

interface PricePerformanceChartProps {
  figi: string;
  securityInfo?: SecurityInfo;
  className?: string;
  compact?: boolean;
  hideTimeframeControls?: boolean;
}

type TimeframeOption = "1D" | "1W" | "1M" | "3M" | "6M" | "1Y";

const TIMEFRAME_OPTIONS: TimeframeOption[] = [
  "1D",
  "1W",
  "1M",
  "3M",
  "6M",
  "1Y",
];

const formatPrice = (value: number): string => `$${value.toFixed(2)}`;

const formatPercentChange = (value: number): string => {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
};

interface TooltipData {
  price: number;
  percent_move: number;
  datetime: string;
}

interface TooltipPayload {
  payload: TooltipData;
}

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const percentChange = data.percent_move;
    const isPositive = percentChange >= 0;

    return (
      <Card size="1" variant="classic">
        <Flex direction="column" p="2" gap="1">
          <Text size="2" weight="bold" highContrast>
            {formatPrice(data.price)}
          </Text>
          <Text size="1" color={isPositive ? "green" : "red"} weight="medium">
            {formatPercentChange(percentChange)}
          </Text>
          <Text size="1" color="gray">
            {format(new Date(data.datetime), "MMM d")}
          </Text>
        </Flex>
      </Card>
    );
  }
  return null;
};

export const timeseriesCache = new Map<
  string,
  { data: TimeseriesData; timestamp: number }
>();
const CACHE_DURATION = 5 * 60 * 1000;

const PricePerformanceChartInner = forwardRef<
  PricePerformanceChartRef,
  PricePerformanceChartProps
>(function PricePerformanceChartInner(
  { figi, securityInfo, compact = false, hideTimeframeControls = false },
  ref
) {
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeOption>(
    "1M"
  );
  const [timeseriesData, setTimeseriesData] = useState<TimeseriesData | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  // Use shared daily data for 1D timeframe
  const {
    data: dailyData,
    isLoading: dailyLoading,
    error: dailyError,
    fetchData: fetchDailyData,
  } = useSharedDailyTimeseries({
    figi: figi || undefined,
    autoFetch: !!figi, // Re-enable auto fetch when we have a FIGI
  });

  const fetchTimeseriesData = useCallback(
    async (timeframe: TimeframeOption) => {
      if (!figi) {
        return;
      }

      // Abort any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Use shared daily data for 1D timeframe
      if (timeframe === "1D" && dailyData) {
        setTimeseriesData(dailyData);
        setLoading(false);
        setError(null);
        return;
      }

      // Check cache first for other timeframes
      const cacheKey = `${figi}-${timeframe}`;
      const cached = timeseriesCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setTimeseriesData(cached.data);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Map timeframe to API interval (using hatch backend enum names)
        let interval = "ONE_MONTH";
        switch (timeframe) {
          case "1D":
            interval = "DAY";
            break;
          case "1W":
            interval = "WEEK";
            break;
          case "1M":
            interval = "ONE_MONTH";
            break;
          case "3M":
            interval = "THREE_MONTHS";
            break;
          case "6M":
            interval = "SIX_MONTHS";
            break;
          case "1Y":
            interval = "YEAR";
            break;
        }

        const params = new URLSearchParams({
          figi,
          interval,
        });

        const url = `/api/v1/timeseries/securities?${params}`;
        const response = await apiClient.request({
          method: "GET",
          url: url,
          signal: abortControllerRef.current.signal,
        });

        if (response.data && response.data.length > 0) {
          const data = response.data[0];

          // Cache the data
          timeseriesCache.set(cacheKey, {
            data: data,
            timestamp: Date.now(),
          });

          setTimeseriesData(data);
        } else {
          setError("No data available for this timeframe");
        }
      } catch (err) {
        // Don't show error for intentionally aborted requests
        if ((err as Error)?.name === "AbortError") {
          console.log("Chart timeseries fetch aborted for:", figi);
          return;
        }
        console.error("Error fetching timeseries data:", err);
        const error = err as { response?: { data?: { message?: string } } };
        setError(error.response?.data?.message || "Failed to fetch price data");
      } finally {
        setLoading(false);
      }
    },
    [figi, dailyData]
  );

  // Expose refetch function via ref
  useImperativeHandle(
    ref,
    () => ({
      refetchData: async () => {
        if (figi) {
          // Clear cache for this figi and refetch
          const cacheKey = `${figi}-${selectedTimeframe}`;
          timeseriesCache.delete(cacheKey);

          // Force fresh fetch of daily data (for 1D timeframe)
          await fetchDailyData(true);

          // Fetch timeseries data for current timeframe
          await fetchTimeseriesData(selectedTimeframe);
        }
      },
    }),
    [figi, selectedTimeframe, fetchTimeseriesData, fetchDailyData]
  );

  // Clear stale data when FIGI changes
  useEffect(() => {
    setTimeseriesData(null);
    setError(null);
    setLoading(false);

    // Clear cache for this figi when it changes
    if (figi) {
      // Clear all cached data for this figi
      TIMEFRAME_OPTIONS.forEach((timeframe) => {
        const cacheKey = `${figi}-${timeframe}`;
        timeseriesCache.delete(cacheKey);
      });
    }
  }, [figi]);

  // Re-enable automatic fetching when FIGI changes
  useEffect(() => {
    if (figi) {
      fetchTimeseriesData(selectedTimeframe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [figi, selectedTimeframe]);

  // Auto-use daily data for 1D timeframe when available
  useEffect(() => {
    if (selectedTimeframe === "1D" && dailyData) {
      setTimeseriesData(dailyData);
      setLoading(dailyLoading);
      setError(dailyError);
    }
  }, [dailyData, dailyLoading, dailyError, selectedTimeframe]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleTimeframeChange = (timeframe: TimeframeOption) => {
    setSelectedTimeframe(timeframe);
  };

  if (!figi) {
    return (
      <Card size="3" style={{ height: "auto" }}>
        <Flex align="center" justify="center" p="6">
          <Text size="2" color="gray" align="center">
            Select a security to view performance
          </Text>
        </Flex>
      </Card>
    );
  }

  const currentPrice = timeseriesData?.price;
  const percentMove = timeseriesData?.percent_move;
  // Filter out data points with price === 0 for cleaner graphs
  const chartData = (timeseriesData?.timeseries || []).filter(
    (d) => d.price !== 0
  );

  // Use previous close as baseline to determine color (matches percent_move calculation)
  const baselinePrice =
    timeseriesData?.previous_close || chartData[0]?.price || currentPrice;
  const isAboveBaseline = (currentPrice || 0) >= (baselinePrice || 0);

  return (
    <Card size="2" style={{ height: "auto" }}>
      <Flex
        direction="column"
        gap={compact ? "1" : "2"}
        p={compact ? "1" : "2"}
      >
        <CardHeader
          title="Performance"
          subtitle={`As of ${
            chartData.length > 0
              ? format(
                  new Date(chartData[chartData.length - 1].datetime),
                  "MMM dd h:mm a"
                )
              : format(new Date(), "MMM dd h:mm a")
          } ET`}
        />

        {!compact && (
          <Text size="2" color="gray">
            {securityInfo?.symbol || "This security"}&apos;s performance over
            the past{" "}
            {(() => {
              switch (selectedTimeframe) {
                case "1D":
                  return "trading day";
                case "1W":
                  return "week";
                case "1M":
                  return "month";
                case "3M":
                  return "3 months";
                case "6M":
                  return "6 months";
                case "1Y":
                  return "year";
                default:
                  return selectedTimeframe;
              }
            })()}
            .
          </Text>
        )}

        {/* Security Info & Price */}
        {securityInfo && (
          <Flex
            direction="column"
            gap={compact ? "1" : "2"}
            my={compact ? "1" : "2"}
          >
            <Heading size={compact ? "4" : "5"} weight="medium" highContrast>
              {securityInfo.symbol}
            </Heading>
            <Text size="2" color="gray">
              {securityInfo.name}
            </Text>

            <Flex align="center" gap="2">
              <Heading size={compact ? "3" : "4"} weight="medium" highContrast>
                {currentPrice ? formatPrice(currentPrice) : "--"}
              </Heading>
              {percentMove !== undefined && (
                <Badge
                  color={isAboveBaseline ? "green" : "red"}
                  variant="soft"
                  size="2"
                >
                  {formatPercentChange(percentMove)} {selectedTimeframe}
                </Badge>
              )}
            </Flex>
          </Flex>
        )}

        {/* Chart */}
        <Box height={compact ? "160px" : "200px"} position="relative">
          {loading ? (
            <Flex
              align="center"
              justify="center"
              height={compact ? "160px" : "200px"}
            >
              <Spinner size="2" />
            </Flex>
          ) : error ? (
            <Flex
              align="center"
              justify="center"
              height={compact ? "160px" : "200px"}
            >
              <Text size="1" color="red">
                {error}
              </Text>
            </Flex>
          ) : chartData.length > 0 ? (
            (() => {
              // Calculate Y-axis domain and ensure baseline is visible
              const prices = chartData.map((d) => d.price);
              const dataMin = Math.min(...prices);
              const dataMax = Math.max(...prices);

              // Include baseline in the domain if it's outside data range
              const minPrice = Math.min(dataMin, baselinePrice);
              const maxPrice = Math.max(dataMax, baselinePrice);

              // Baseline price is already calculated above in the main scope

              const createDynamicGradient = () => {
                const stops = [];

                // Use green if current price is above previous close, red otherwise
                const color = isAboveBaseline ? "#569346" : "#DC2626";

                stops.push(<stop key="0" offset="0%" stopColor={color} />);
                stops.push(<stop key="100" offset="100%" stopColor={color} />);

                return stops;
              };

              const chartHeight = compact ? 160 : 200;

              return (
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <LineChart
                    key={`${selectedTimeframe}-${figi}`}
                    data={chartData}
                    margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient
                        id="dynamicLineGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        {createDynamicGradient()}
                      </linearGradient>
                    </defs>

                    <XAxis
                      dataKey="datetime"
                      axisLine={false}
                      tickLine={false}
                      tick={false}
                    />
                    <YAxis hide domain={[minPrice, maxPrice]} />
                    <Tooltip content={<CustomTooltip />} />

                    {/* Dotted baseline for 0% change (previous close) */}
                    <ReferenceLine
                      y={baselinePrice}
                      stroke="var(--gray-9)"
                      strokeDasharray="4 4"
                      strokeWidth={1.5}
                      opacity={0.8}
                      label={
                        selectedTimeframe === "1D"
                          ? {
                              value: "Previous Close",
                              position: "right",
                              offset: 10,
                              style: {
                                fontSize: "11px",
                                fill: "var(--gray-11)",
                                fontWeight: "500",
                              },
                            }
                          : undefined
                      }
                    />

                    {/* Single line with dynamic gradient stroke */}
                    <Line
                      type="monotone"
                      dataKey="price"
                      stroke="url(#dynamicLineGradient)"
                      strokeWidth={2.5}
                      dot={false}
                      connectNulls={true}
                      isAnimationActive={!loading}
                      animationDuration={600}
                    />
                  </LineChart>
                </ResponsiveContainer>
              );
            })()
          ) : (
            <Flex
              align="center"
              justify="center"
              height={compact ? "160px" : "200px"}
            >
              <Text size="1" color="gray">
                No chart data available
              </Text>
            </Flex>
          )}
        </Box>

        {/* Timeframe Selector */}
        {!hideTimeframeControls && (
          <Flex gap="1" wrap="wrap" mt={compact ? "1" : "2"}>
            {TIMEFRAME_OPTIONS.map((option) => (
              <Button
                key={option}
                variant={selectedTimeframe === option ? "solid" : "soft"}
                size="2"
                radius="full"
                onClick={() => handleTimeframeChange(option)}
                color={selectedTimeframe === option ? "blue" : "gray"}
              >
                {option}
              </Button>
            ))}
          </Flex>
        )}
      </Flex>
    </Card>
  );
});

const PricePerformanceChart = forwardRef<
  PricePerformanceChartRef,
  PricePerformanceChartProps
>(function PricePerformanceChart(props, ref) {
  return (
    <PricePerformanceChartInner
      key={props.figi || "no-figi"}
      ref={ref}
      {...props}
    />
  );
});

export default PricePerformanceChart;
